import { inject, Injectable } from '@angular/core';
import { getOrderUuid, getSelectedExhibitionId } from '@app/app.reducer';
import { Store } from '@ngrx/store';
import {
  BookingContingentReservation,
  BookingParkingReservation,
  BookingTariff,
  BookingTariffType,
  PostProductBooking
} from '@products/models/booking.model';
import { ProductType } from '@products/models/products.model';
import { Tariff, TariffPersonalizationType, TariffUuids } from '@products/models/tariff.model';
import { RedeemedVoucher, RemoveVoucherTariffBooking, VoucherCode } from '@products/models/voucher.model';
import { BookingHolderService } from '@products/services/booking-holder.service';
import { ContingentBookingService } from '@products/services/contingent-booking.service';
import { ParkingBookingService } from '@products/services/parking-booking.service';
import { TariffBookingService } from '@products/services/tariff-booking.service';
import { getBookedTariffTypeAndTariffByTicketPersonIdAndVoucherCode } from '@store/products/booking/product/tariff/tariff-booking.selectors';
import { combineLatest, first, map, Observable } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class VoucherBookingService {
  private readonly store = inject(Store);
  private readonly tariffBookingService = inject(TariffBookingService);
  private readonly bookingHolderService = inject(BookingHolderService);
  private readonly contingentBookingService = inject(ContingentBookingService);
  private readonly parkingBookingService = inject(ParkingBookingService);

  removeVoucherTariffBookings$(removeVoucherTariff: RemoveVoucherTariffBooking): Observable<{
    bookingTariffTypes: BookingTariffType[];
    removePostProductBookings: PostProductBooking[];
  }> {
    return combineLatest([
      this.store.select(getSelectedExhibitionId),
      this.store.select(getOrderUuid),
      this.store.select(
        getBookedTariffTypeAndTariffByTicketPersonIdAndVoucherCode(
          removeVoucherTariff.ticketPersonId,
          removeVoucherTariff.voucherCode
        )
      )
    ]).pipe(this.tariffBookingService.prepareRemoveTariffBookingPipe(removeVoucherTariff.ticketPersonId));
  }

  removeReleasedVoucherTariffBookings$(removeVoucher: RemoveVoucherTariffBooking): Observable<{
    bookedVoucherTariffTypes: BookingTariffType[];
    voucherCode: VoucherCode;
  }> {
    return this.store
      .select(
        getBookedTariffTypeAndTariffByTicketPersonIdAndVoucherCode(
          removeVoucher.ticketPersonId,
          removeVoucher.voucherCode
        )
      )
      .pipe(
        first(),
        map((bookedVoucherTariffType) => {
          const bookedVoucherTariff =
            bookedVoucherTariffType &&
            bookedVoucherTariffType.tariffs.find(
              (bookedVoucherTariff) => bookedVoucherTariff.ticketPersonId === removeVoucher.ticketPersonId
            );

          if (bookedVoucherTariff) {
            bookedVoucherTariff.count = 0;
            bookedVoucherTariff.tariffUuids = [];
          }

          const bookedVoucherTariffTypes = bookedVoucherTariff ? [bookedVoucherTariffType] : [];

          return {
            bookedVoucherTariffTypes,
            voucherCode: removeVoucher.voucherCode
          };
        })
      );
  }

  mapRedeemedVoucherToBookingTariffType(
    redeemedVoucher: RedeemedVoucher,
    voucherTariff: Tariff,
    validatedVoucherCount: number
  ): BookingTariffType {
    const { voucherCode, voucherType, quantity, isAnonymous, availableTickets, ticketLimit } = redeemedVoucher;

    const {
      name,
      price,
      ticketPersonId,
      ticketPersonTypeId,
      ticketTypeId,
      ticketTypeName,
      hasDaySellLimit,
      isParkingTicket,
      isLicensePlateMandatory,
      documentUploadType
    } = voucherTariff;

    const personalizationType = isAnonymous ? TariffPersonalizationType.Anonymous : TariffPersonalizationType.Mandatory;

    const tariffUuids: TariffUuids = this.bookingHolderService.setTariffUuids(validatedVoucherCount, false);

    const contingentReservations: BookingContingentReservation[] =
      this.contingentBookingService.setBookingContingentReservations(
        validatedVoucherCount,
        hasDaySellLimit,
        tariffUuids
      );

    const parkingReservations: BookingParkingReservation[] = this.parkingBookingService.setBookingParkingReservations(
      validatedVoucherCount,
      isParkingTicket,
      isLicensePlateMandatory,
      tariffUuids
    );

    const bookingTariff: BookingTariff = {
      ticketTypeId,
      ticketPersonId,
      tariffNumber: null,
      ticketTypeName,
      ticketPersonTypeId,
      packageNumber: null,
      packageIndex: null,
      ticketName: name,
      availableTickets,
      ticketLimit,
      voucherCode,
      voucherType,
      voucherCountLimit: quantity,
      personalizationType,
      documentUploadType,
      activatedTimestamp: Date.now(),
      tariffUuids,
      count: validatedVoucherCount,
      price,
      discountedPrice: 0,
      discountPercentage: 0,
      promoCodeDiscounts: null,
      workshops: [],
      contingentReservations,
      amanoParkingReservations: [],
      parkingReservations
    };

    const bookingTariffType: BookingTariffType = {
      productType: ProductType.Tariff,
      productTypeName: ticketTypeName,
      ticketTypeId: redeemedVoucher.groupId,
      tariffs: [bookingTariff]
    };

    return bookingTariffType;
  }
}
